import React, { useEffect, useState } from 'react';
import { useSchoolData } from './SchoolDataProvider';
import { todayIso } from './calendarMath';
import { DAY_LABELS, shortDate, weekDates as weekDatesFor } from './placement';
import AssignmentCard from './AssignmentCard';
import WeekBoard from './WeekBoard';

const VIEW_MODES = [
  { id: 'byDay', label: 'By day' },
  { id: 'byBlock', label: 'By subject' },
];

// This week — and, since the "My Board" tab was folded in, the surface where
// the week actually gets laid out. "By day" for the current week IS the board
// (see WeekBoard.jsx); browsing to another week drops to a read-only rendering
// of it, because a tile can never be placed outside its own week: the
// placement rule in firestore.rules can't write weekNumber, so a cross-week
// placement would leave date and weekNumber disagreeing and the tile would
// vanish from both weeks' views.
export default function WeekPage() {
  const {
    assignments, calendar, loading, seeded, courseFor, familyId,
    weekNumber: currentWeekNumber, unplacedThisWeek,
  } = useSchoolData();

  const [weekOffset, setWeekOffset] = useState(0);
  const [viewMode, setViewMode] = useState('byDay');

  // The board wants the wider container; the by-subject list doesn't.
  const wide = viewMode === 'byDay' && weekOffset === 0;
  useEffect(() => {
    const el = document.querySelector('.school-content');
    if (!el) return;
    el.classList.toggle('school-content--wide', wide);
    return () => el.classList.remove('school-content--wide');
  }, [wide]);

  if (loading) return <p className="settings-note">Loading this week…</p>;
  if (!seeded) {
    return <p className="settings-note">No school calendar set up yet — ask a parent to seed the year plan.</p>;
  }

  const weekNumber = currentWeekNumber + weekOffset;
  const monday = calendar.weeks[weekNumber - 1];
  const thisWeek = monday ? assignments.filter(a => a.weekNumber === weekNumber) : [];
  const isCurrentWeek = weekOffset === 0;

  const byDate = {};
  for (const a of thisWeek) {
    if (!a.date) continue;
    (byDate[a.date] ||= []).push(a);
  }

  // "What's on deck this week" at a glance, by subject area — using
  // subjectArea (already on every course doc) rather than day, so unplaced
  // tiles show up here too.
  const byBlock = {};
  for (const a of thisWeek) {
    const area = courseFor(a)?.subjectArea || 'Other';
    (byBlock[area] ||= []).push(a);
  }
  const blocks = Object.keys(byBlock).sort();

  const unplacedHere = isCurrentWeek
    ? unplacedThisWeek.length
    : thisWeek.filter(a => a.placementMode === 'student-placed' && !a.date).length;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '12px 0' }}>
        <button className="btn btn--ghost btn--small" onClick={() => setWeekOffset(o => o - 1)}>‹ Prev week</button>
        <span className="db-eyebrow" style={{ flex: 1, textAlign: 'center' }}>
          Week {weekNumber}{isCurrentWeek && ' · This week'}
        </span>
        <button className="btn btn--ghost btn--small" onClick={() => setWeekOffset(o => o + 1)}>Next week ›</button>
      </div>
      {!isCurrentWeek && (
        <button className="btn btn--ghost btn--small" style={{ marginBottom: 8 }} onClick={() => setWeekOffset(0)}>
          Back to this week
        </button>
      )}

      {!monday || weekNumber < 1 || weekNumber > calendar.weeks.length ? (
        <p className="settings-note">Outside the school year.</p>
      ) : thisWeek.length === 0 ? (
        <p className="settings-note">Nothing scheduled this week.</p>
      ) : (
        <>
          <div className="pickup-pattern-tabs" style={{ marginBottom: 12 }}>
            {VIEW_MODES.map(m => (
              <button key={m.id} type="button"
                className={`pickup-pattern-tab ${viewMode === m.id ? 'active' : ''}`}
                onClick={() => setViewMode(m.id)}>{m.label}</button>
            ))}
          </div>

          {viewMode === 'byDay' && (
            isCurrentWeek
              ? <WeekBoard />
              : <ReadOnlyWeek
                  monday={monday}
                  weekNumber={weekNumber}
                  byDate={byDate}
                  unplaced={unplacedHere}
                  courseFor={courseFor}
                  familyId={familyId}
                  holidays={calendar.holidays}
                />
          )}

          {viewMode === 'byBlock' && blocks.map(area => {
            const items = byBlock[area];
            const done = items.filter(a => a.status === 'complete').length;
            const unplaced = items.filter(a => !a.date).length;
            return (
              <div key={area} className="db-card" style={{ padding: '12px 16px', marginBottom: 8 }}>
                <div className="db-block-name" style={{ fontSize: 16 }}>{area}</div>
                <div className="db-block-meta">
                  {done}/{items.length} done{unplaced > 0 ? ` · ${unplaced} still to place` : ''}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

// Another week, looked at rather than planned. Same five columns so the shape
// is familiar, but nothing here is droppable or draggable.
function ReadOnlyWeek({ monday, weekNumber, byDate, unplaced, courseFor, familyId, holidays }) {
  const dates = weekDatesFor(monday);
  const today = todayIso();

  return (
    <>
      {unplaced > 0 && (
        <p className="db-item-sub" style={{ margin: '0 0 10px' }}>
          {unplaced} session{unplaced === 1 ? '' : 's'} for week {weekNumber} still need a day — place them once that week starts.
        </p>
      )}
      <div className="school-week-board">
        {dates.map((date, i) => {
          const dow = i + 1;
          const items = (byDate[date] || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          const holiday = holidays?.[date];
          return (
            <div key={date} className="db-card db-day" style={{ padding: 10, opacity: holiday ? 0.6 : 1 }}>
              <div className="db-eyebrow" style={{ marginBottom: 6 }}>
                {DAY_LABELS[dow]} · {shortDate(date)}{date === today && ' · Today'}
              </div>
              {holiday ? (
                <p className="db-item-sub" style={{ margin: 0 }}>{holiday} — no school</p>
              ) : items.length === 0 ? (
                <p className="db-item-sub" style={{ margin: 0 }}>Nothing yet</p>
              ) : (
                <ul className="db-items" style={{ padding: 0 }}>
                  {items.map(a => (
                    <AssignmentCard key={a.id} a={a} course={courseFor(a)} familyId={familyId} />
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
